/** @typedef {'left' | 'center' | 'right'} PayslipAlign */
/** @typedef {'sm' | 'md' | 'lg'} PayslipFontSize */
/** @typedef {'normal' | 'bold'} PayslipFontWeight */
/** @typedef {'normal' | 'italic'} PayslipFontStyle */
/** @typedef {'default' | 'muted' | 'dark' | 'light' | 'primary' | 'indigo' | 'violet' | 'sky' | 'teal' | 'success' | 'danger' | 'warning' | 'info' | 'navy' | 'burgundy' | 'forest' | 'orange' | 'rose' | 'brown' | 'custom'} PayslipTextColor */
/** @typedef {'none' | 'uppercase' | 'capitalize'} PayslipTextTransform */

/**
 * @typedef {Object} PayslipBlock
 * @property {string} id
 * @property {string} type
 * @property {PayslipAlign} [align]
 * @property {string} [text]
 * @property {PayslipFontSize} [fontSize]
 * @property {PayslipFontWeight} [fontWeight]
 * @property {PayslipFontStyle} [fontStyle]
 * @property {PayslipTextColor} [textColor]
 * @property {string} [customTextColor]
 * @property {PayslipTextTransform} [textTransform]
 * @property {string} [componentId]
 * @property {string} [taxPolicyId]
 * @property {number} [logoWidth]
 * @property {boolean} [hidden]
 */

/**
 * @typedef {Object} PayslipTemplate
 * @property {number} version
 * @property {PayslipBlock[]} blocks
 * @property {boolean} finalized
 * @property {string | null} finalizedAt
 */

import {
  normalizeCustomHex,
  normalizeTextColorKey,
  PAYSLIP_TEXT_COLOR_CUSTOM,
  resolveTextColorHex,
} from "./payslipTextColors";

export const PAYSLIP_TEMPLATE_VERSION = 3;

export const FORMATTABLE_BLOCK_TYPES = new Set([
  "customText",
  "companyName",
  "title",
  "disclaimer",
  "payPeriod",
]);

export const EDITABLE_TEXT_BLOCK_TYPES = new Set([
  "customText",
  "companyName",
  "title",
  "disclaimer",
  "payPeriod",
]);

/** @returns {Pick<PayslipBlock, 'fontSize' | 'fontWeight' | 'fontStyle' | 'textColor' | 'textTransform'>} */
export const defaultTextFormat = () => ({
  fontSize: "md",
  fontWeight: "normal",
  fontStyle: "normal",
  textColor: "default",
  textTransform: "none",
});

/** @deprecated Use getBlockTextStyle from PayslipTextFormatInspector */
export const getCustomTextStyle = (block) => ({
  fontSize: block.fontSize === "sm" ? "0.72rem" : block.fontSize === "lg" ? "1rem" : "0.82rem",
  fontWeight: block.fontWeight === "bold" ? 700 : 400,
  fontStyle: block.fontStyle === "italic" ? "italic" : "normal",
  textTransform:
    block.textTransform === "uppercase" ? "uppercase" : block.textTransform === "capitalize" ? "capitalize" : "none",
  color: resolveTextColorHex(block),
});

const normalizeFontSize = (raw) => (raw === "sm" || raw === "lg" ? raw : "md");
const normalizeFontWeight = (raw) => (raw === "bold" ? "bold" : "normal");
const normalizeFontStyle = (raw) => (raw === "italic" ? "italic" : "normal");
const normalizeTextColor = (raw) => normalizeTextColorKey(raw);
const normalizeTextTransform = (raw) =>
  raw === "uppercase" || raw === "capitalize" ? raw : "none";

/** @param {string} type @param {Record<string, unknown>} row */
const parseTextFormatFields = (type, row) => {
  if (!FORMATTABLE_BLOCK_TYPES.has(type)) return {};
  const textColor = normalizeTextColor(row.textColor);
  return {
    fontSize: normalizeFontSize(row.fontSize),
    fontWeight: normalizeFontWeight(row.fontWeight),
    fontStyle: normalizeFontStyle(row.fontStyle),
    textColor,
    customTextColor:
      textColor === PAYSLIP_TEXT_COLOR_CUSTOM
        ? normalizeCustomHex(row.customTextColor)
        : undefined,
    textTransform: normalizeTextTransform(row.textTransform),
  };
};

export const BLOCK_TYPES = {
  LOGO: "logo",
  COMPANY_NAME: "companyName",
  TITLE: "title",
  PAY_PERIOD: "payPeriod",
  EMPLOYEE_INFO: "employeeInfo",
  INCOME_SECTION: "incomeSection",
  DEDUCTION_SECTION: "deductionSection",
  GROSS_TOTAL: "grossTotal",
  NET_PAY: "netPay",
  DISCLAIMER: "disclaimer",
  CUSTOM_TEXT: "customText",
  DIVIDER: "divider",
  COMPONENT_LINE: "componentLine",
  TAX_LINE: "taxLine",
  STRUCTURE_LABEL: "structureLabel",
  SPACER: "spacer",
};

/** @param {string} [prefix] */
export const newBlockId = (prefix = "blk") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** @returns {PayslipTemplate} */
export const createDefaultTemplate = () => ({
  version: PAYSLIP_TEMPLATE_VERSION,
  finalized: false,
  finalizedAt: null,
  blocks: [
    { id: newBlockId("logo"), type: BLOCK_TYPES.LOGO, align: "left", logoWidth: 120 },
    { id: newBlockId("co"), type: BLOCK_TYPES.COMPANY_NAME, align: "right", ...defaultTextFormat() },
    { id: newBlockId("sp"), type: BLOCK_TYPES.SPACER, align: "center" },
    { id: newBlockId("ti"), type: BLOCK_TYPES.TITLE, align: "center", ...defaultTextFormat() },
    { id: newBlockId("pp"), type: BLOCK_TYPES.PAY_PERIOD, align: "center" },
    { id: newBlockId("dv"), type: BLOCK_TYPES.DIVIDER, align: "center" },
    { id: newBlockId("em"), type: BLOCK_TYPES.EMPLOYEE_INFO, align: "left" },
    { id: newBlockId("in"), type: BLOCK_TYPES.INCOME_SECTION, align: "left" },
    { id: newBlockId("de"), type: BLOCK_TYPES.DEDUCTION_SECTION, align: "left" },
    { id: newBlockId("gr"), type: BLOCK_TYPES.GROSS_TOTAL, align: "left" },
    { id: newBlockId("np"), type: BLOCK_TYPES.NET_PAY, align: "left" },
    { id: newBlockId("ds"), type: BLOCK_TYPES.DISCLAIMER, align: "center", ...defaultTextFormat() },
  ],
});

/** Creates an empty custom text block ready to drop on the canvas. */
export const createTextBoxBlock = () => ({
  id: newBlockId("text"),
  type: BLOCK_TYPES.CUSTOM_TEXT,
  align: "left",
  text: "",
  ...defaultTextFormat(),
});

/** @param {unknown} raw */
export const normalizeTemplate = (raw) => {
  if (!raw || typeof raw !== "object") return createDefaultTemplate();
  const r = /** @type {Record<string, unknown>} */ (raw);
  const blocksRaw = Array.isArray(r.blocks) ? r.blocks : [];
  const blocks = blocksRaw
    .filter((b) => b && typeof b === "object" && typeof b.type === "string")
    .map((b) => {
      const row = /** @type {Record<string, unknown>} */ (b);
      const type = String(row.type);
      return {
        id: String(row.id || newBlockId()),
        type,
        align: row.align === "left" || row.align === "right" || row.align === "center" ? row.align : "left",
        text: row.text != null ? String(row.text) : undefined,
        componentId: row.componentId != null ? String(row.componentId) : undefined,
        taxPolicyId: row.taxPolicyId != null ? String(row.taxPolicyId) : undefined,
        logoWidth: Number.isFinite(Number(row.logoWidth)) ? Number(row.logoWidth) : 120,
        hidden: Boolean(row.hidden),
        ...parseTextFormatFields(type, row),
      };
    });
  return {
    version: PAYSLIP_TEMPLATE_VERSION,
    finalized: Boolean(r.finalized),
    finalizedAt: r.finalizedAt != null ? String(r.finalizedAt) : null,
    blocks: blocks.length ? blocks : createDefaultTemplate().blocks,
  };
};
